import { List, Map } from 'immutable';
import {
  constants as jobsConstants,
  records as jobsRecords,
  types as jobsTypes,
  utils as jobUtils,
} from '@saucelabs/sl-module-jobs';
import { transition, statusToEvent, Event, State } from '@saucelabs/ds-job-card';

import * as types from './types';
import {
  INITIAL_RDC_OFFSET,
  DEFAULT_PAGE,
  DEFAULT_PAGE_SIZE,
} from './constants';

const initialState = Map({
  automated: List(),
  automatedLoading: true,
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PAGE_SIZE,
  perfMetrics: Map(),
  rdc: List(),
  rdcError: {},
  rdcLoading: true,
  rdcMoreAvailable: false,
  rdcOffset: INITIAL_RDC_OFFSET,
  runningStates: Map(),
  totalJobs: 0,
});

const toggleLoader = (mutable, turnOnLoader = true) => (
  mutable
    .set('automatedLoading', turnOnLoader)
    .set('rdcLoading', turnOnLoader)
);

const jobsReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case types.ARCHIVED_RDC_JOBS_GET.SUCCESS: {
      const jobs = payload.jobs
        ?.map((job) => new jobsRecords.RdcJobRecord({
          ...job,
          // NOTE: Archived jobs has a slightly different shape than resto jobs
          // so do some normalization here.
          assigned_tunnel_id: job.tunnel_id,
          browser: job.browser_name?.toLowerCase(),
          build: job.build_name,
          device_name: job.device,

          // NOTE: creation_time is not exactly the same time as start_time, but
          // this is currently the only one available
          start_time: job.creation_time,
          status: (job.status === 'in progress' ? 'running' : job.status),
        }));

      const jobStates = jobs?.map((job) => {
        const { id } = job;
        const currentState = state.get('runningStates').get(id) || State.INIT;
        const event = statusToEvent(job.getStatus());
        const newState = transition(currentState, event);

        return [id, newState];
      });

      return state.withMutations((mutable) => (
        mutable
          .set('rdc', List(jobs))
          .set('rdcLoading', false)
          .set('runningStates', Map(jobStates))
          .set('totalJobs', payload.total)
      ));
    }

    case types.ARCHIVED_JOBS_GET.SUCCESS: {
      const jobs = payload.jobs
        ?.map((job) => new jobsRecords.JobRecord({
          ...job,
          // NOTE: Archived jobs has a slightly different shape than resto jobs
          // so do some normalization here.
          assigned_tunnel_id: job.tunnel_id,
          browser: job.browser_name?.toLowerCase(),
          build: job.build_name,
          device_name: job.device,
          // NOTE: using a string template causes an eslint error that requires a package update:
          // see: https://github.com/eslint/eslint/issues/13542
          // eslint-disable-next-line prefer-template
          os: job.os + ' ' + job.os_version,
          status: (job.status === 'in progress' ? 'running' : job.status),
        }));

      const jobStates = jobs?.map((job) => {
        const { id } = job;
        const currentState = state.get('runningStates').get(id) || State.INIT;
        const event = statusToEvent(job.getStatus());
        const newState = transition(currentState, event);

        return [id, newState];
      });

      return state.withMutations((mutable) => (
        mutable
          .set('automated', List(jobs))
          .set('automatedLoading', false)
          .set('runningStates', Map(jobStates))
          .set('totalJobs', payload.total)
      ));
    }
    case types.CHANGE_PAGE:
      return state.withMutations((mutable) => {
        mutable.set('page', payload);
        toggleLoader(mutable, true);
      });
    case types.CHANGE_PER_PAGE:
      return state.withMutations((mutable) => {
        mutable.set('perPage', payload);
        mutable.set('page', 1);
        toggleLoader(mutable, true);
      });
    case types.RESET_PAGE:
      return state.withMutations((mutable) => {
        mutable.set('page', initialState.get('page'));
        toggleLoader(mutable, true);
      });
    case jobsTypes.JOBS_GET.SUCCESS:
    case jobsTypes.AUTOMATED_JOBS_GET.SUCCESS: {
      const perfMetrics = state.get('perfMetrics');
      const jobs = payload.jobs
        ?.map((job) => new jobsRecords.JobRecord({
          ...job,
          performance_metrics: perfMetrics.get(job.id),
        }));

      const jobStates = jobs?.map((job) => {
        const { id } = job;
        const currentState = state.get('runningStates').get(id) || State.INIT;
        const event = statusToEvent(job.getStatus());
        const newState = transition(currentState, event);

        return [id, newState];
      });

      return state.withMutations((mutable) => (
        mutable
          .set('automated', List(jobs))
          .set('automatedLoading', false)
          .set('runningStates', Map(jobStates))
      ));
    }
    case jobsTypes.JOBS_GET.FAILURE: {
      return state.withMutations((mutable) => (
        mutable
          .set('automatedLoading', false)
      ));
    }
    case jobsTypes.PERF_METRICS_GET.SUCCESS: {
      const jobs = state.get('automated');
      if (payload?.items?.length && jobs?.size) {
        const perfMetrics = jobUtils.groupPerformanceByJobId(payload.items);
        const automatedJobsListIncludingPerf = jobUtils.addPerformanceMetricsInJobs({
          perfMetrics, jobs,
        });
        return state.withMutations((mutable) => (
          mutable
            .set('automated', automatedJobsListIncludingPerf)
            .set('perfMetrics', Map(perfMetrics))
        ));
      }
      return state;
    }
    case jobsTypes.UP_JOBS_GET.REQUEST:
      return state.set('rdcError', initialState.get('rdcError'));
    case jobsTypes.UP_JOBS_GET.SUCCESS: {
      const jobs = payload.entities.map((job) => new jobsRecords.RdcJobRecord(job));
      return state.withMutations((mutable) => (
        mutable
          .set('rdc', List(jobs))
          .set('rdcLoading', false)
          .set('rdcMoreAvailable', payload.metaData.moreAvailable)
      ));
    }
    case jobsTypes.UP_JOBS_GET.FAILURE: {
      const { response: { data: serverMessage, status: code } = {} } = payload;
      return state.withMutations((mutable) => {
        mutable.set('rdcError', { code, serverMessage });
        mutable.set('rdcLoading', false);
      });
    }
    case jobsTypes.JOB_STOP.REQUEST:
    case jobsTypes.JOB_STOP_RDC.REQUEST: {
      const { id } = payload;
      const runningStates = state.get('runningStates');
      const currentState = runningStates.get(id);
      const newState = transition(currentState, Event.STOP);

      return state.withMutations((mutable) => {
        mutable.setIn(['runningStates', id], newState);
      });
    }
    case jobsTypes.JOB_STOP.FAILURE: {
      const { id } = payload;
      const runningStates = state.get('runningStates');
      const currentState = runningStates.get(id);
      const newState = transition(currentState, Event.RESUME);

      return state.withMutations((mutable) => {
        mutable.setIn(['runningStates', id], newState);
      });
    }
    case jobsTypes.JOB_STOP_RDC.FAILURE: {
      const { config } = payload;
      const id = config?.url?.split('/')[2];
      const runningStates = state.get('runningStates');
      const currentState = runningStates.get(id);

      if (!currentState) {
        return state;
      }

      const newState = transition(currentState, Event.RESUME);

      return state.withMutations((mutable) => {
        mutable.setIn(['runningStates', id], newState);
      });
    }
    case types.DECREASE_RDC_OFFSET:
      return state.withMutations((mutable) => {
        mutable.set('rdcOffset', state.get('rdcOffset') - jobsConstants.QUERY_LIMIT);
        toggleLoader(mutable, true);
      });
    case types.INCREASE_RDC_OFFSET:
      return state.withMutations((mutable) => {
        mutable.set('rdcOffset', state.get('rdcOffset') + jobsConstants.QUERY_LIMIT);
        toggleLoader(mutable, true);
      });
    case types.CLEAR_RDC_OFFSET:
      return state.withMutations((mutable) => {
        mutable.set('rdcOffset', initialState.get('rdcOffset'));
        toggleLoader(mutable, true);
      });
    case types.SHOW_LOADER:
      return state.withMutations((mutable) => {
        toggleLoader(mutable, true);
      });
    default:
      return state;
  }
};

export default jobsReducer;
